import React, { MutableRefObject, useEffect, useRef, useState } from 'react'

import {
  ReloadOutlined,
  LoadingOutlined,
  QuestionOutlined,
  ExclamationCircleOutlined,
  ExclamationCircleFilled
} from '@ant-design/icons'
import {
  Button,
  Table,
  Tag,
  TableColumnsType,
  Modal,
  Tooltip,
  Skeleton,
  Space,
  Popconfirm
} from 'antd'
import { ModalFunc } from 'antd/lib/modal/confirm'
import { AxiosError } from 'axios'
import { useParams, Link } from 'react-router-dom'
import useSWR from 'swr'

import { OldreportComparisonReport } from '@clinic/client'
import {
  deleteComparisonReport,
  getComparisonReport,
  getComparisonReportList
} from '~/api'
import { useMutation } from '~/hooks/use-mutation'
import { comparisonReportTelemetry } from '~/telemetry/comparison-report'
import { formatDateTime } from '~/utils/date-time'

enum StatusType {
  Unscheduled = 'unscheduled',
  Running = 'running',
  Success = 'success',
  Failed = 'failed',
  Deleted = 'deleted',
  Pending = 'pending'
}
const tagColorMap: { [k: string]: string } = {
  [StatusType.Running]: 'processing',
  [StatusType.Pending]: 'processing',
  [StatusType.Success]: 'success',
  [StatusType.Failed]: 'error'
}

const PAGE_SIZE = 10

export const getComparisonReportListKey = (
  oid: string,
  cid: string,
  page: number
) => ['comparison_report/list', oid, cid, page]

export const ReportTable: React.FC = () => {
  const { orgId, cid } = useParams<'orgId' | 'cid'>()
  const [page, setPage] = useState(1)
  const {
    data: listRes,
    error,
    mutate
  } = useSWR(getComparisonReportListKey(orgId!, cid!, page), () =>
    getComparisonReportList(orgId!, cid!, PAGE_SIZE, page)
  )
  const { run: deleteReport } = useMutation(deleteComparisonReport)
  const modalRef: MutableRefObject<ReturnType<ModalFunc> | null> = useRef(null)

  const columns: TableColumnsType<OldreportComparisonReport> = [
    {
      title: 'ID',
      dataIndex: 'id'
    },
    {
      title: 'T1 Time Range',
      key: 'timerange1',
      render: (val, rec) => (
        <p className='mb-0'>
          {formatDateTime(rec.args?.startTime1!)} ~{' '}
          {formatDateTime(rec.args?.endTime1!)}
        </p>
      )
    },
    {
      title: 'T2 Time Range',
      key: 'timerange2',
      render: (val, rec) => (
        <p className='mb-0'>
          {formatDateTime(rec.args?.startTime2!)} ~{' '}
          {formatDateTime(rec.args?.endTime2!)}
        </p>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 140,
      render: (val: StatusType, rec) => (
        <>
          <Tag className='rounded-lg' color={tagColorMap[val] || 'default'}>
            {`${val.slice(0, 1).toUpperCase()}${val.slice(1)}`}
          </Tag>
          {(val === StatusType.Running || val === StatusType.Pending) && (
            <Tooltip placement='bottom' title='Refresh Status'>
              <Button
                size='small'
                shape='circle'
                icon={<ReloadOutlined />}
                onClick={() => mutate()}
              />
            </Tooltip>
          )}
          {val === StatusType.Failed && (
            <Button
              size='small'
              shape='circle'
              icon={<QuestionOutlined />}
              onClick={async () => {
                const modal = Modal.error({
                  title: 'Error',
                  content: <Skeleton.Input style={{ width: '150px' }} active />,
                  icon: <LoadingOutlined />,
                  okText: 'Close',
                  maskClosable: true,
                  afterClose: () => (modalRef.current = null)
                })
                modalRef.current = modal

                try {
                  const { data } = await getComparisonReport(
                    orgId!,
                    cid!,
                    String(rec.id!)
                  )
                  modal.update({
                    content:
                      (data?.data as any)?.errorMessage || 'No error message',
                    icon: <ExclamationCircleOutlined />
                  })
                } catch (e) {
                  modal.update({
                    content:
                      (e as AxiosError).response?.data.message ||
                      'No error message',
                    icon: <ExclamationCircleOutlined />
                  })
                }
              }}
            />
          )}
        </>
      )
    },
    {
      title: 'Operations',
      key: 'ops',
      render: (val, rec, idx) => {
        return (
          <Space>
            <Link
              // hack for styles
              // react-router-dom `Link` does not support `disabled` prop but antd global style works
              {...{ disabled: rec.status !== StatusType.Success }}
              className='mr-2 select-none'
              to={`${rec.id}`}
              target='_blank'
              onClick={(e) => {
                if (rec.status !== StatusType.Success) {
                  return e.preventDefault()
                }
                comparisonReportTelemetry.viewReport({
                  orgId: orgId!,
                  clusterId: cid!,
                  reportId: String(rec.id)
                })
              }}
            >
              View
            </Link>

            {(rec.status === StatusType.Success ||
              rec.status === StatusType.Failed) && (
              <Popconfirm
                onConfirm={async () => {
                  await deleteReport({
                    oid: orgId!,
                    cid: cid!,
                    rid: String(rec.id)
                  })
                  comparisonReportTelemetry.deleteReport({
                    orgId: orgId!,
                    clusterId: cid!,
                    reportId: String(rec.id)
                  })
                  mutate()
                }}
                title='Are you sure?'
                okType='danger'
                icon={<ExclamationCircleFilled style={{ color: 'red' }} />}
              >
                <Button className='px-0' type='link' danger>
                  Delete
                </Button>
              </Popconfirm>
            )}
          </Space>
        )
      }
    }
  ]

  useEffect(
    () => () => {
      if (!modalRef.current) {
        return
      }
      modalRef.current.destroy()
    },
    []
  )

  return (
    <Table<OldreportComparisonReport>
      rowKey='id'
      loading={!error && !listRes}
      columns={columns}
      dataSource={listRes?.data?.reports || []}
      pagination={{
        position: ['bottomRight'],
        total: listRes?.data?.total || 0,
        current: page,
        onChange: (p) => setPage(p)
      }}
      expandable={{ expandedRowRender: ExpandRow }}
    />
  )
}

const ExpandRow = (rec: OldreportComparisonReport) => {
  const columns: TableColumnsType<OldreportComparisonReport> = [
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      render: (val) => formatDateTime(val)
    },
    {
      title: 'Creator',
      dataIndex: 'creator'
    }
  ]

  return (
    <Table<OldreportComparisonReport>
      rowKey='id'
      columns={columns}
      dataSource={[rec]}
      pagination={false}
      size='small'
    />
  )
}
